import { useCallback, useEffect, useRef } from "react";

export function useDebouncedCallback<TArgs extends readonly unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number,
): { trigger: (...args: TArgs) => void; cancel: () => void } {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const delayRef = useRef(delayMs);
  delayRef.current = delayMs;

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const trigger = useCallback(
    (...args: TArgs) => {
      cancel();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fnRef.current(...args);
      }, delayRef.current);
    },
    [cancel],
  );

  useEffect(() => cancel, [cancel]);

  return { trigger, cancel };
}
